/* Copyright (c) 2026 Xperi. All Rights Reserved. */

/**
 * UI state for the debug-only override overlay (web-app version + mock host).
 *
 * Deliberately holds no *routing* state of its own for either override: the
 * active version lives in the `x-app-version` cookie and nowhere else, and the
 * active mock host lives in `localStorage.OVERRIDE_MOCK_HOST` and nowhere
 * else. `activeVersion` and `activeMockHost` here are read-only echoes of
 * those, captured when the overlay opens, purely so it can show what the
 * launch is currently running against.
 *
 * The two overrides are kept strictly parallel and strictly independent —
 * their own input, their own Apply/Reset pair and their own error line —
 * because they answer different questions (*which build* vs *which backend*)
 * and are far more often changed one at a time than together.
 */
export interface AppVersionOverrideState {
  /** True while the override overlay is on screen. */
  isVisible: boolean;
  /** Current contents of the version text field. */
  versionInput: string;
  /** Current contents of the mock-host field, as `<host:port>|<token>`. */
  mockHostInput: string;
  /**
   * The version this launch is running as, as resolved at overlay-open time.
   * Empty string when the launch carries no version (default/latest build).
   */
  activeVersion: string;
  /**
   * The mock host this launch is routed through, as resolved at overlay-open
   * time. Empty string when no override is stored and the app is talking to
   * the real backend.
   */
  activeMockHost: string;
  /**
   * Validation/error message for the version override, or empty string when
   * there is none. Separate from the mock-host message so a rejected version
   * is reported under the field that caused it.
   */
  versionErrorMessage: string;
  /** Validation/error message for the mock-host override, or empty string. */
  mockHostErrorMessage: string;
  /** Index of the focused control — see `AppVersionOverrideFocus`. */
  focusedIndex: number;
}

/**
 * Focus order of the overlay's controls, matching the rendered layout: each
 * override is a field followed by its own Apply/Reset row, version first.
 */
export enum AppVersionOverrideFocus {
  VERSION_INPUT = 0,
  VERSION_APPLY = 1,
  VERSION_RESET = 2,
  MOCK_HOST_INPUT = 3,
  MOCK_HOST_APPLY = 4,
  MOCK_HOST_RESET = 5
}

/** Number of focusable controls — the focus arithmetic's upper bound. */
export const APP_VERSION_OVERRIDE_FOCUS_COUNT: number = 6;
